/*------------------------------------*\
  #Details Disclosure
\*------------------------------------*/

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content =
      this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut(event) {
    setTimeout(() => {
      const activeElement = document.activeElement;
      if (
        !this.contains(activeElement) ||
        (activeElement.tagName === 'A' && this.content.contains(activeElement))
      ) {
        return;
      }
      this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach(animation => animation.play());
    } else {
      this.animations.forEach(animation => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle
      .querySelector('summary')
      .setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/*------------------------------------*\
  #Header Menu
\*------------------------------------*/

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (
      document.documentElement.style.getPropertyValue(
        '--header-bottom-position-desktop'
      ) !== ''
    )
      return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

if (!customElements.get('drawer-disclosure')) {
  class DrawerDisclosure extends HTMLElement {
    constructor() {
      super();
      this.mainDetailsToggle = this.querySelector('details');
      this.summary = this.mainDetailsToggle.querySelector('summary');
      this.summary.addEventListener('click', this.toggleDrawer.bind(this));
      this.content = this.querySelector('[data-drawer-content]');
      const isOpen = this.mainDetailsToggle.hasAttribute('open');
      // Menu drawer panels are positioned/animated entirely via CSS;
      // inline height is only for the height-animated accordion variant.
      if (!this.closest('.menu-drawer__navigation')) {
        this.content.style.height = isOpen ? 'auto' : 0;
      }
      const closeOthersAttr = this.getAttribute('data-close-others');
      this.closeOtherDrawers = closeOthersAttr !== 'false';

      // Track pointer interactions inside content so focusout doesn't auto-close
      this.isInteractingWithin = false;
      const markInteracting = () => (this.isInteractingWithin = true);
      const clearInteracting = () => setTimeout(() => (this.isInteractingWithin = false));
      this.content.addEventListener('mousedown', markInteracting, true);
      this.content.addEventListener('touchstart', markInteracting, { passive: true, capture: true });
      this.content.addEventListener('click', clearInteracting, true);

      // Prevent clicks inside content from bubbling to any global outside-click handlers
      this.content.addEventListener('click', event => {
        event.stopPropagation();
      });
      
      this.content.addEventListener('mouseup', clearInteracting, true);
      this.content.addEventListener('touchend', clearInteracting, { passive: true, capture: true });
      this.mainDetailsToggle.addEventListener(
        'focusout',
        this.onFocusOut.bind(this)
      );
      this.mainDetailsToggle.addEventListener(
        'keydown',
        this.onKeyDown.bind(this)
      );
      this.animationLength = 450;
    }

    connectedCallback() {
      if (Shopify.designMode) {
        document.addEventListener(
          'shopify:block:select',
          this.oncontentBlockSelect.bind(this)
        );
      }

      // Optionally open the first drawer of this component group on page load
      const openFirstAttr = this.getAttribute('data-open-first-drawer-by-default');
      const shouldOpenFirst = openFirstAttr !== null && openFirstAttr !== 'false';
      if (shouldOpenFirst) {
        // Group key is the prefix before the first dash in data-id, e.g. "location" for "location-1"
        const currentId = this.mainDetailsToggle && this.mainDetailsToggle.dataset ? this.mainDetailsToggle.dataset.id : null;
        if (currentId && currentId.includes('-')) {
          const groupKey = currentId.split('-')[0];

          // Ensure we only auto-open the first instance per group across the page
          window.__drawerFirstOpenedByGroup = window.__drawerFirstOpenedByGroup || {};
          if (!window.__drawerFirstOpenedByGroup[groupKey]) {
            // Defer to allow all instances to connect before querying
            setTimeout(() => {
              const firstDetails = document.querySelector(`drawer-disclosure details[data-id^="${groupKey}-"]`);
              if (firstDetails) {
                this.openDrawer(firstDetails);
                window.__drawerFirstOpenedByGroup[groupKey] = true;
              }
            });
          }
        }
      }

      if (
        this.mainDetailsToggle &&
        this.mainDetailsToggle.hasAttribute('open') &&
        this.summary
      ) {
        requestAnimationFrame(() => this.openDrawer(this.summary));
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        this.closeDrawer(this);
      }
    }

    onFocusOut(event) {
      const nextFocused = event.relatedTarget;
      // If focus is moving within the component, do nothing
      if (nextFocused && this.contains(nextFocused)) return;

      // Defer to allow click/mousedown handlers to mark interaction state
      // setTimeout(() => {
      //   if (this.isInteractingWithin) return;
      //   const activeElement = document.activeElement;
      //   if (this.contains(activeElement)) return;
      //   this.closeDrawer(this);
      // });
    }

    toggleDrawer(event) {
      event.preventDefault();

      if (this.mainDetailsToggle.getAttribute('open')) {
        this.closeDrawer(this);
      } else {
        // Open clicked drawer first for reliability
        this.openDrawer(event.currentTarget);
        if (this.closeOtherDrawers) {
          // Then close any other open drawers (accordion behavior)
          document.querySelectorAll('drawer-disclosure details[open]').forEach(openDetails => {
            if (openDetails !== this.mainDetailsToggle) {
              const host = openDetails.closest('drawer-disclosure');
              host && host.closeDrawer(host);
            }
          });
        }
      }
    }

    closeDrawer(pElem) {
      if (!pElem) return;

      const detailsElement = pElem.querySelector('details');
      if (!detailsElement) return;

      const contentElement = pElem.querySelector('[data-drawer-content]');
      const isMenuDrawerNavigation = this.closest('.menu-drawer__navigation');

      const summary = pElem.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', false);
      }

      if (isMenuDrawerNavigation) {
        // For all drawers in menu navigation, the CSS handles the sliding animation
        // Remove the class to trigger slide back animation
        this.classList.remove('drawer-active');
        const navigation = this.closest('.menu-drawer__navigation');
        if (navigation) {
          navigation.classList.remove('drawer-active');
        }
        // Just remove the open attribute after a short delay to allow animation
        setTimeout(() => {
          detailsElement.removeAttribute('open');
        }, 300); // Match CSS transition duration
      } else {
        // For drawers outside menu navigation, animate height
        if (contentElement) {
          contentElement.style.height = 0;
        }
        setTimeout(() => {
          detailsElement.removeAttribute('open');
        }, this.animationLength);
      }
    }

    openDrawer(pDrawer) {
      const parentDetails = pDrawer.closest('details');

      if (parentDetails && !parentDetails.getAttribute('open')) {
        parentDetails.setAttribute('open', true);
        const contentAnswer = parentDetails.querySelector(
          '[data-drawer-content]'
        );

        // Check if this drawer is inside the menu drawer navigation
        const isMenuDrawerNavigation = this.closest('.menu-drawer__navigation');
        
        if (isMenuDrawerNavigation) {
          const navigation = this.closest('.menu-drawer__navigation');

          if (contentAnswer) {
            // Force a paint in the offscreen state, then activate on the next
            // frame so the CSS slide-in transition actually runs.
            void contentAnswer.offsetWidth;
            requestAnimationFrame(() => {
              this.classList.add('drawer-active');
              if (navigation) {
                navigation.classList.add('drawer-active');
              }
            });

            const dismissTriggers = contentAnswer.querySelectorAll(
              '[data-back-button], [data-subpanel-dismiss]'
            );
            dismissTriggers.forEach((trigger) => {
              const newTrigger = trigger.cloneNode(true);
              trigger.parentNode.replaceChild(newTrigger, trigger);
              newTrigger.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.closeDrawer(this);
              });
            });
          }
        } else {
          // For drawers outside menu navigation, use the height animation
          requestAnimationFrame(() => {
            const innerContent = contentAnswer.querySelector(
              '[data-drawer-content-inner]'
            );

            if (innerContent) {
              const height = innerContent.offsetHeight;
              contentAnswer.style.height = `${height}px`;
            }
          });
        }

        parentDetails
          .querySelector('summary')
          .setAttribute('aria-expanded', true);
        this.activeDrawer = parentDetails.dataset.id;
      }
    }

    oncontentBlockSelect(event) {
      event.preventDefault();
      setTimeout(() => this.openDrawer(event.target), 200);
    }
  }

  customElements.define('drawer-disclosure', DrawerDisclosure);
}
